// Frontend-owned Image Generator dictionaries (Excel Module 0 + style/garment options).
// Report catalog — macros, colour palettes, pattern trends, assets — comes from the backend.
export const GENRES = ["Fashion", "Accessories"] as const;

export const MARKETS = ["Women", "Men"] as const;

export const CATEGORIES = ["Ethnic RTW", "Western RTW"] as const;

// 2. Complete Silhouettes Mapping (100% Excel Dataset)
/** Image Generator stored influence for “From The Library”. */
export const LIBRARY_INFLUENCE = "Generic";

/** Image Generator stored influence for “From The Forecast”. */
export const FORECAST_INFLUENCE = "New trend";

/** Image Generator stored influence for “Upload Specific image”. */
export const UPLOAD_INFLUENCE = "Upload Specific image";

export const INFLUENCE_OPTIONS = [
  LIBRARY_INFLUENCE,
  FORECAST_INFLUENCE,
  UPLOAD_INFLUENCE,
] as const;

export const isForecastInfluence = (value?: string | null): boolean =>
  (value || "").trim() === FORECAST_INFLUENCE;

/** Image Generator “From The Library” influence (stored as Generic). */
export const isLibraryInfluence = (value?: string | null): boolean =>
  (value || "").trim() === LIBRARY_INFLUENCE;

/** Image Generator “Upload Specific image” influence. */
export const isUploadInfluence = (value?: string | null): boolean =>
  (value || "").trim() === UPLOAD_INFLUENCE;

/** Forecast / Upload: follow-ups are Material + Length only (Image Generator). */
export const isMaterialLengthOnlyInfluence = (value?: string | null): boolean =>
  isForecastInfluence(value) || isUploadInfluence(value);

export const FORECAST_REPORT_COPY = {
  silhouetteTitle: "Silhouette Report",
  detailedTitle: "Detailed Report",
  silhouetteToggle: "Silhouette",
  detailedToggle: "Detailed",
  silhouetteEmpty: "No silhouette-report slides for these filters.",
  detailedEmpty: "No detailed-report slides for these filters.",
  silhouetteRequired: "Select a silhouette report.",
  /** Kept for copy reuse; detailed sketches are optional in Image Generator. */
  detailedRequired: "Select a detailed report.",
  /** Silhouette required when available; detailed is optional. */
  sketchRequired: "Select a silhouette sketch.",
  loading: "Loading forecast reports…",
  chooseTrend: "Choose Trend",
  chooseDetailTrend: "Choose Detail Trend",
  specificSketchOnly: "Specific Sketch Only",
  removeSilhouette: "Remove silhouette",
  removeDetailed: "Remove detailed",
  sketchPlaceholder: "Sketch",
} as const;

/** Soft-confirm copy when Generate is clicked with incomplete style dropdowns. */
export const INCOMPLETE_STYLE_DROPDOWNS_CONFIRM_TITLE =
  "Are you sure you want to proceed?";
export const INCOMPLETE_STYLE_DROPDOWNS_CONFIRM_BODY =
  "Selecting all dropdowns will give you the desired results";

/** UI label for Category; API still uses Ethnic RTW / Western RTW. */
export const categoryDisplayLabel = (value?: string | null): string =>
  (value || "").replace(/\s*RTW\s*$/i, "").trim() || (value || "").trim();

/** UI label for Influence; API still stores Generic / New trend / Upload Specific image. */
export const influenceDisplayLabel = (value?: string | null): string => {
  if (value === LIBRARY_INFLUENCE) {
    return "From The Library";
  }
  if (value === FORECAST_INFLUENCE) {
    return "From The Forecast";
  }
  if (value === UPLOAD_INFLUENCE) {
    return "Upload Specific Image";
  }
  return (value || "").trim();
};

/** Digits only — a catalog id, not a trend name. */
export const isNumericId = (value: string) => /^\d+$/.test(value.trim());

/** Joined theme text with bare ids removed. Empty when nothing is a name. */
export const trendDisplayName = (value?: string | null): string => {
  const trimmed = String(value || "").trim();
  if (!trimmed || isNumericId(trimmed)) {
    return "";
  }
  const parts = trimmed
    .split(/\s*(?:,|·)\s*/)
    .map(part => part.trim())
    .filter(Boolean);
  const names = parts.filter(part => !isNumericId(part));
  if (!names.length) {
    return "";
  }
  if (names.length === parts.length) {
    return trimmed;
  }
  return names.join(", ");
};

/** "Surface" / "Surface trend" are section labels, not a slide title. */
export const isGenericSurfaceLabel = (value: string) =>
  /^(surface|surface trend)$/i.test(value.trim());

export const PRODUCT_TYPES: Record<string, Record<string, string[]>> = {
  Women: {
    "Western RTW": [
      "Tshirts",
      "Tops",
      "Coord Set",
      "Dresses",
      "Overalls",
      "Jeans",
      "Trousers",
      "Skirts",
      "Blazers",
      "Jackets",
      "Sweaters",
      "Sweatshirts",
      "Vests",
      "Shorts",
    ],
    "Ethnic RTW": [
      "Tops",
      "Dresses",
      "Kurtas",
      "Kurta Sets (3-piece)",
      "Overalls",
      "Trousers",
      "Skirts",
      "Jackets",
      "Vests",
      "Sarees",
      "Shorts",
    ],
  },
  Men: {
    "Western RTW": [
      "Tshirts",
      "Shirts",
      "Jeans",
      "Trousers",
      "Overalls",
      "Blazers",
      "Jackets",
      "Sweaters",
      "Sweatshirts",
      "Vests",
      "Shorts",
    ],
    "Ethnic RTW": [
      "Shirts",
      "Kurtas",
      "Kurta Sets (3-piece)",
      "Overalls",
      "Trousers",
      "Jackets",
      "Vests",
      "Shorts",
    ],
  },
};

/** Women Western RTW — style types exactly as sheet. */
export const STYLE_TYPES_WOMEN_WESTERN: Record<string, string[]> = {
  Tshirts: ["Bodysuit", "Crew", "Henley", "Polo", "Tank", "Tube"],
  Tops: [
    "Blouson",
    "Bodysuit",
    "Bralette",
    "Camisole",
    "Apron",
    "Corset",
    "Peplum",
    "Shirt",
    "Tunic",
    "Vest",
    "Wrap",
    "Kaftan",
    "Peasant",
    "Cape",
  ],
  "Coord Set": [
    "Blouson",
    "Bralette",
    "Camisole",
    "Apron",
    "Corset",
    "Peplum",
    "Shirt",
    "Tunic",
    "Vest",
    "Wrap",
    "Kaftan",
    "Peasant",
    "Cape",
  ],
  Dresses: [
    "A-Line dress",
    "Apron dress",
    "Babydoll dress",
    "Ball gown dress",
    "Balloon dress",
    "Blazer dress",
    "Bodycon",
    "Camisole dress",
    "Corset dress",
    "Empire waist dress",
    "Maxi dress",
    "Mermaid dress",
    "Polo dress",
    "Shift dress",
    "Skater dress",
    "Tshirt dress",
    "Wrap dress",
    "Bouffant",
    "Blouson",
    "Peasant",
    "Drop Waist",
    "Tiered Dress",
    "Cape",
    "Sheath",
    "Tent",
    "Kaftan",
  ],
  Overalls: ["Dungaree", "Jumpsuit", "Romper", "Wrap"],
  Jeans: [
    "Bell Bottoms",
    "Bootcut",
    "Cargo",
    "Straight",
    "Tapered",
    "Wide leg",
    "Jodhpurs",
    "Joggers",
  ],
  Trousers: [
    "Capri",
    "Cargo pants",
    "Cigarette pants",
    "Draped Pants",
    "Harem pants",
    "Joggers",
    "Leggings",
    "Palazzo",
    "Pyjamas",
    "Straight pants",
    "Slacks",
    "Track pants",
    "Dhoti pants",
    "Gharara",
    "Jodhpurs",
    "Pegged",
    "Wide leg",
    "Culottes",
    "Hot pants",
    "Bootcut",
    "Bell bottoms",
  ],
  Skirts: [
    "A-line skirt",
    "Antariya",
    "Pleated",
    "Umbrella",
    "Draped skirt",
    "Godet skirt",
    "Handkerchief skirt",
    "Mermaid",
    "Pencil",
    "Tiered",
    "Tulip skirt",
    "Wrap",
    "Ballgown",
  ],
  Blazers: ["Tuxedo blazer", "Single breasted", "Double breasted"],
  Jackets: [
    "Biker jacket",
    "Bolero",
    "Bomber",
    "Capes",
    "Cropped jacket",
    "Gilet",
    "Jerkin",
    "Parkas",
    "Pea coat",
    "Poncho",
    "Puffer jacket",
    "Raincoat jacket",
    "Shearling jacket",
    "Shrugs",
    "Track jacket",
    "Trench coat",
    "Trucker jacket",
    "Varsity jacket",
    "Waterfall shrug",
    "Wrap Jacket",
    "Windbreaker",
    "Single breasted jacket",
    "Double breasted jacket",
  ],
  Sweaters: ["Cardigan", "Pullover", "Wrap", "Tunic", "Poncho", "Shrugs"],
  Sweatshirts: ["Hoodie", "Bomber", "Cropped jacket", "Varsity jacket"],
  Vests: ["Single breasted jacket", "Double breasted", "Waistcoat"],
  Shorts: [
    "Cargo pants",
    "Bermudas",
    "Boxer",
    "Lean shorts",
    "Hot Pants",
    "Culottes",
    "Pleated",
  ],
};

/** Women Ethnic RTW — style types exactly as sheet. */
export const STYLE_TYPES_WOMEN_ETHNIC: Record<string, string[]> = {
  Tops: [
    "Blouson",
    "Bodysuit",
    "Bralette",
    "Camisole",
    "Apron",
    "Corset",
    "Peplum",
    "Shirt",
    "Tunic",
    "Vest",
    "Wrap",
    "Choli",
    "Kaftan",
    "Peasant",
    "Cape",
  ],
  Dresses: [
    "A-Line dress",
    "Apron dress",
    "Babydoll dress",
    "Ball gown dress",
    "Balloon dress",
    "Blazer dress",
    "Bodycon",
    "Camisole dress",
    "Corset dress",
    "Empire waist dress",
    "Maxi dress",
    "Mermaid dress",
    "Polo dress",
    "Shift dress",
    "Skater dress",
    "Tshirt dress",
    "Wrap dress",
    "Bouffant",
    "Blouson",
    "Peasant",
    "Drop Waist",
    "Tiered Dress",
    "Cape",
    "Sheath",
    "Tent",
    "Kaftan",
  ],
  Kurtas: [
    "Abha Kurta",
    "A-Line Kurta",
    "Anarkali Kurta",
    "Angrakha",
    "Cape kurta",
    "Column",
    "Highlow kurta",
    "Kaftan kurta",
    "Kalidar Kurta",
    "Shirt kurta",
    "Tiered kurta",
    "Wrap Kurta",
    "Draped kurta",
    "Pathani",
    "Straight",
  ],
  "Kurta Sets (3-piece)": [
    "Abha Kurta Set",
    "A-Line Kurta Set",
    "Anarkali Kurta Set",
    "Angrakha Kurta Set",
    "Column Kurta Set",
    "Highlow Kurta Set",
    "Kaftan Coord Set",
    "Kalidar Kurta Set",
    "Tiered Kurta Set",
    "Wrap Kurta Set",
    "Pathani Kurta Set",
    "Draped Kurta Set",
    "Shirt Kurta Set",
  ],
  Overalls: ["Dungaree", "Jumpsuit", "Romper", "Wrap"],
  Trousers: [
    "Capri",
    "Cargo pants",
    "Cigarette pants",
    "DrapedPants",
    "Harem pants",
    "Joggers",
    "Leggings",
    "Palazzo",
    "Pyjamas",
    "Straight pants",
    "Slacks",
    "Track pants",
    "Dhoti pants",
    "Gharara",
    "Jodhpurs",
    "Pegged",
    "Wide leg",
    "Culottes",
    "Hot pants",
    "Bootcut",
    "Bell bottoms",
    "Chinos",
    "Chudidar",
    "Cowl pants",
    "Dhoti",
    "Tailored pants",
    "Patiala",
    "Salwar",
    "Lungi",
  ],
  Skirts: [
    "A-line skirt",
    "Antariya",
    "Pleated",
    "Umbrella",
    "Draped skirt",
    "Godet skirt",
    "Handkerchief skirt",
    "Mermaid",
    "Pencil",
    "Tiered",
    "Tulip skirt",
    "Lehenga",
    "Wrap",
    "Ballgown",
  ],
  Jackets: [
    "Bolero",
    "Bomber",
    "Capes",
    "Cropped jacket",
    "Gilet",
    "Pea coat",
    "Poncho",
    "Shrugs",
    "Trench coat",
    "Waterfall shrug",
    "Wrap Jacket",
    "Single breasted jacket",
    "Double breasted jacket",
  ],
  Vests: ["Single breasted", "Double breasted", "Waistcoat"],
  Sarees: [
    "Saree",
    "Half saree",
    "Mermaid saree",
    "Ruffled",
    "Nauvari",
    "Dhoti saree",
    "Pant saree",
  ],
  Shorts: [
    "Cargo pants",
    "Bermudas",
    "Boxer",
    "Lean shorts",
    "Hot Pants",
    "Culottes",
    "Pleated",
  ],
};

/** @deprecated Prefer STYLE_TYPES_WOMEN_WESTERN / STYLE_TYPES_WOMEN_ETHNIC via styleOptionsFor. */
export const STYLE_TYPES_WOMEN: Record<string, string[]> =
  STYLE_TYPES_WOMEN_WESTERN;

/** Men silhouettes — separate from Women (shared product names must not inherit women-only styles). */
export const STYLE_TYPES_MEN_WESTERN: Record<string, string[]> = {
  Tshirts: ["Crew", "Henley", "Polo", "Tank"],
  Shirts: ["Button Down", "Crew", "Tuxedo", "Utility", "Wrap", "Regular"],
  Jeans: [
    "Bell Bottoms",
    "Bootcut",
    "Cargo",
    "Straight",
    "Tapered",
    "Jodhpurs",
    "Joggers",
    "Wide leg",
  ],
  Trousers: [
    "Cargo pants",
    "Chinos",
    "Chudidar",
    "Cigarette pants",
    "Cowl pants",
    "Dhoti",
    "Dhoti pants",
    "Tailored pants",
    "Harem pants",
    "Joggers",
    "Patiala",
    "Salwar",
    "Straight pants",
    "Trackpants",
    "Boxer shorts",
    "Cargo shorts",
    "Shorts",
    "Jodhpurs",
    "Pegged pants",
    "Wide leg pants",
    "Bermuda shorts",
    "Capris",
    "Bootcut",
    "Bell bottoms",
  ],
  Overalls: ["Dungaree", "Jumpsuit", "Wrap"],
  Blazers: [
    "Sports blazer",
    "Tuxedo blazer",
    "Single-breasted blazer",
    "Double-breasted blazer",
    "Jodhpuri suit sets",
    "Sherwani",
    "Bandgala",
    "Bandi",
  ],
  Jackets: [
    "Anorak",
    "Biker",
    "Bomber jacket",
    "Cardigan",
    "Single-breasted",
    "Double breasted",
    "Duffel jacket",
    "Field jacket",
    "Gilet",
    "Jerkin",
    "Macintosh",
    "Parka",
    "Peacoat",
    "Puffer",
    "Raincoat jacket",
    "Shearling jacket",
    "Track jacket",
    "Trenchcoat",
    "Trucker jacket",
    "Varsity jacket",
    "Windbreaker",
    "Shacket",
  ],
  Sweaters: ["Cardigan", "Pullover", "Wrap", "Tunic", "Poncho", "Shrugs"],
  Sweatshirts: ["Hoodie", "Bomber", "Cropped jacket", "Varsity jacket"],
  Vests: ["Single breasted jacket", "Double breasted jacket", "Waistcoat"],
  Shorts: [
    "Cargo pants",
    "Bermudas",
    "Boxer",
    "Lean shorts",
    "Hot Pants",
    "Culottes",
    "Pleated",
  ],
};

export const STYLE_TYPES_MEN_ETHNIC: Record<string, string[]> = {
  Shirts: ["Button Down", "Crew", "Tuxedo", "Utility", "Wrap", "Regular"],
  Kurtas: [
    "Angrakh",
    "Pathani",
    "Straight",
    "Draped Kurta",
    "Shirt Kurta",
    "Kalidhar Kurta",
    "Highlow kurta",
    "Wrap Kurta",
  ],
  "Kurta Sets (3-piece)": [
    "Angrakh Kurta Set",
    "Pathani Kurta Set",
    "Column Kurta Set",
    "Draped Kurta Kurta Set",
    "Shirt Kurta Kurta Set",
    "Kalidhar Kurta Kurta Set",
    "Highlow kurta Kurta Set",
    "Wrap Kurta Kurta Set",
  ],
  Overalls: ["Dungaree", "Jumpsuit", "Wrap"],
  Trousers: [
    "Capri",
    "Cargo pants",
    "Cigarette pants",
    "DrapedPants",
    "Harem pants",
    "Joggers",
    "Leggings",
    "Palazzo",
    "Pyjamas",
    "Straight pants",
    "Slacks",
    "Track pants",
    "Dhoti pants",
    "Gharara",
    "Jodhpurs",
    "Pegged",
    "Wide leg",
    "Culottes",
    "Hot pants",
    "Bootcut",
    "Bell bottoms",
    "Lungi",
  ],
  Jackets: [
    "Bolero",
    "Bomber",
    "Capes",
    "Cropped jacket",
    "Gilet",
    "Pea coat",
    "Poncho",
    "Shrugs",
    "Trench coat",
    "Waterfall shrug",
    "Wrap Jacket",
    "Single breasted jacket",
    "Double breasted jacket",
  ],
  Vests: ["Single breasted jacket", "Double breasted jacket", "Waistcoat"],
  Shorts: [
    "Cargo pants",
    "Bermudas",
    "Boxer",
    "Lean shorts",
    "Hot Pants",
    "Culottes",
    "Pleated",
  ],
};

/** @deprecated Prefer STYLE_TYPES_MEN_WESTERN / STYLE_TYPES_MEN_ETHNIC via styleOptionsFor(market, product, category). */
export const STYLE_TYPES_MEN: Record<string, string[]> =
  STYLE_TYPES_MEN_WESTERN;

/** Market → product → style types. Prefer this over the flat legacy shape. */
export const STYLE_TYPES_BY_MARKET: Record<string, Record<string, string[]>> = {
  Women: STYLE_TYPES_WOMEN_WESTERN,
  Men: STYLE_TYPES_MEN_WESTERN,
};

/** @deprecated Prefer styleOptionsFor(market, product, category). Kept for any flat lookups. */
export const STYLE_TYPES: Record<string, string[]> = STYLE_TYPES_WOMEN_WESTERN;

/** Normalize legacy / Collection Build product labels onto IG keys. */
const STYLE_PRODUCT_ALIASES: Record<string, string> = {
  Coords: "Coord Set",
  "Co-ords": "Coord Set",
  "Co-ord Sets": "Coord Set",
  "Kurta Sets": "Kurta Sets (3-piece)",
};

export const styleOptionsFor = (
  market: string,
  product: string,
  category?: string
): string[] => {
  if (!product?.trim()) {
    return [];
  }
  const resolved = STYLE_PRODUCT_ALIASES[product] || product;
  if (market === "Men") {
    const primary =
      category === "Ethnic RTW"
        ? STYLE_TYPES_MEN_ETHNIC
        : STYLE_TYPES_MEN_WESTERN;
    if (primary[resolved]?.length) {
      return primary[resolved];
    }
    // Collection Build omits category — fall back to the other catalog.
    if (category === undefined && STYLE_TYPES_MEN_ETHNIC[resolved]?.length) {
      return STYLE_TYPES_MEN_ETHNIC[resolved];
    }
  }
  if (market === "Women") {
    const primary =
      category === "Ethnic RTW"
        ? STYLE_TYPES_WOMEN_ETHNIC
        : STYLE_TYPES_WOMEN_WESTERN;
    if (primary[resolved]?.length) {
      return primary[resolved];
    }
    if (category === undefined && STYLE_TYPES_WOMEN_ETHNIC[resolved]?.length) {
      return STYLE_TYPES_WOMEN_ETHNIC[resolved];
    }
  }
  const byMarket = STYLE_TYPES_BY_MARKET[market]?.[resolved];
  if (byMarket?.length) {
    return byMarket;
  }
  return ["Regular"];
};

// 3. Complete Follow-Up Attributes Dataset (100% Excel Dataset)
export type DropdownOption = {
  value: string;
  label: string;
  /** Recommended / sheet-bold — show first and render bold in the menu. */
  emphasized?: boolean;
};

/** Women — bold on sheet → first + emphasized. */
const NECKLINE_RECOMMENDED_WOMEN = [
  "Round",
  "Regular V",
  "Split",
  "Band collar",
  "Crew neck",
  "Shirt collar",
  "Boat neck",
] as const;

/** Men — bold on sheet → first + emphasized. */
const NECKLINE_RECOMMENDED_MEN = [
  "Asymmetric",
  "Band collar",
  "Crew neck",
  "Polo collar",
  "Point collar",
  "Regular V",
] as const;

/** @deprecated Prefer necklineOptionsFor(market). */
export const NECKLINE_PRIORITY = NECKLINE_RECOMMENDED_WOMEN;

const NECKLINE_ALL = [
  "Round",
  "Regular V",
  "Split",
  "Band collar",
  "Crew neck",
  "Shirt collar",
  "Boat neck",
  "Asymmetric",
  "Bucket V",
  "Polo collar",
  "Cowl neck",
  "Cuban collar",
  "Halter",
  "High",
  "Keyhole",
  "Knotch collar",
  "Off shoulder",
  "Peterpan collar",
  "Plunge neck",
  "Point collar",
  "Queen Anne",
  "Roll collar",
  "Scoop",
  "Shawl collar",
  "Square",
  "Surplice",
  "Sweetheart",
  "Turtle",
  "Wide V",
  "Hooded",
  "Kimono",
  "Cut-Away",
] as const;

/** Flat list (Women order). Prefer necklineOptionsFor(market) for UI. */
export const NECKLINE_OPTIONS = NECKLINE_ALL;

/** Women — bold on sheet → first + emphasized. */
const SLEEVE_RECOMMENDED_WOMEN = [
  "Short sleeve",
  "Long sleeve",
  "3/4th sleeve",
  "Sleeveless",
  "Shirt sleeve",
] as const;

/** Men — bold on sheet → first + emphasized. */
const SLEEVE_RECOMMENDED_MEN = [
  "Short sleeve",
  "Long sleeve",
  "3/4th sleeve",
  "Shirt sleeve",
  "Sleeveless",
] as const;

/** @deprecated Prefer sleeveOptionsFor(market). */
export const SLEEVE_PRIORITY = [
  "Cap",
  ...SLEEVE_RECOMMENDED_WOMEN.slice(0, 3),
  "Sleeveless",
  "Spaghetti strap",
  "Shirt sleeve",
  "Off-shoulder",
  "Cold shoulder",
] as const;

const SLEEVE_ALL = [
  "Cap",
  "Short sleeve",
  "Long sleeve",
  "3/4th sleeve",
  "Sleeveless",
  "Spaghetti strap",
  "Shirt sleeve",
  "Off-shoulder",
  "Cold shoulder",
  "Batwing",
  "Bell",
  "Bishop",
  "Butterfly",
  "Cowl",
  "Drop shoulder",
  "Dupatta sleeve",
  "Flare",
  "Juliet",
  "Kimono",
  "Leg-o-mutton",
  "Magyar",
  "Peasant",
  "Petal",
  "Poet's",
  "Puff",
  "Raglan",
  "Dolman",
  "Cape Sleeve",
] as const;

/** Flat list. Prefer sleeveOptionsFor(market) for UI. */
export const SLEEVE_OPTIONS = SLEEVE_ALL;

const orderWithRecommended = (
  recommended: readonly string[],
  all: readonly string[]
): DropdownOption[] => {
  const seen = new Set<string>();
  const options: DropdownOption[] = [];
  for (const value of recommended) {
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    options.push({ value, label: value, emphasized: true });
  }
  for (const value of all) {
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    options.push({ value, label: value });
  }
  return options;
};

export const necklineOptionsFor = (market?: string): DropdownOption[] =>
  orderWithRecommended(
    market === "Men" ? NECKLINE_RECOMMENDED_MEN : NECKLINE_RECOMMENDED_WOMEN,
    NECKLINE_ALL
  );

export const sleeveOptionsFor = (market?: string): DropdownOption[] =>
  orderWithRecommended(
    market === "Men" ? SLEEVE_RECOMMENDED_MEN : SLEEVE_RECOMMENDED_WOMEN,
    SLEEVE_ALL
  );

export const MATERIALS = [
  "Brocade",
  "Satin",
  "Raw silk",
  "Chiffon",
  "Corduroy",
  "Velvet",
  "Fur",
  "Sherpa",
  "Leather",
  "Suiting",
  "Denim",
  "Tweed",
  "Nylon",
  "Poplin",
  "Chambray",
  "Oxford",
  "Cotton twill",
  "Rayon",
  "Linen",
  "Seersucker",
  "Swiss dot",
  "Organza",
  "Flannel",
  "Cheesecloth",
  "Metallic (Lurex)",
  "Lace",
  "Tissue",
  "Schiffli",
  "Birdseye",
  "Fleece",
  "Rib",
  "Melange",
  "Spandex",
  "Mesh",
  "Cable knit",
  "Single Jersey",
  "Crotchet",
] as const;

/**
 * Tshirts / Sweaters / Sweatshirts only — knit/jersey fabrics (not the full MATERIALS list).
 */
export const KNIT_TOP_MATERIALS = [
  "Birdseye",
  "Fleece",
  "Rib",
  "Melange",
  "Spandex",
  "Mesh",
  "Cable knit",
  "Single Jersey",
  "Crotchet",
] as const;

const KNIT_TOP_PRODUCT_TYPES = new Set(["Tshirts", "Sweaters", "Sweatshirts"]);

const byLabelAsc = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

/** Alphabetically sorted copies used by Material dropdowns. */
const MATERIALS_ALPHABETICAL = [...MATERIALS].sort(byLabelAsc);
const KNIT_TOP_MATERIALS_ALPHABETICAL = [...KNIT_TOP_MATERIALS].sort(
  byLabelAsc
);

/** Material dropdown options for a product type (knit tops get the restricted set). */
export const materialsOptionsFor = (
  productType?: string
): readonly string[] => {
  const resolved =
    STYLE_PRODUCT_ALIASES[String(productType || "").trim()] ||
    String(productType || "").trim();
  if (KNIT_TOP_PRODUCT_TYPES.has(resolved)) {
    return KNIT_TOP_MATERIALS_ALPHABETICAL;
  }
  return MATERIALS_ALPHABETICAL;
};

export const WASH_OPTIONS = [
  "Stone wash",
  "Acid Wash",
  "Distressed",
  "Bleach",
  "Light Wash",
  "Raw Denim",
  "Tinted",
] as const;

export const FIT_OPTIONS = [
  "Fit and Flare",
  "Boxy",
  "Regular",
  "Skinny",
  "Slim",
] as const;

export type FollowUpField =
  | "neckline"
  | "sleeve"
  | "material"
  | "fit"
  | "wash"
  | "length";

const BOTTOM_PRODUCTS = new Set([
  "Jeans",
  "Trousers",
  "Shorts",
  "Overalls",
  "Vests",
]);

export const CROP_LENGTH_OPTIONS = [
  "Crop",
  "Waist length",
  "Hip-length",
] as const;

export const LONG_LENGTH_OPTIONS = [
  "Hip-length",
  "Knee length",
  "Calf-length",
  "Floor length",
] as const;

const CROP_LENGTH_PRODUCTS = new Set([
  "Tshirts",
  "Tops",
  "Shirts",
  "Jackets",
  "Sweaters",
  "Sweatshirts",
  "Coord Set",
  "Co-ord Sets",
  "Coords",
  "Co-ords",
]);

const LONG_LENGTH_PRODUCTS = new Set([
  "Dresses",
  "Skirts",
  "Kurtas",
  "Kurta Sets",
  "Kurta Sets (3-piece)",
]);

export const getLengthOptions = (productType?: string): string[] => {
  if (!productType) {
    return [];
  }
  if (CROP_LENGTH_PRODUCTS.has(productType)) {
    return [...CROP_LENGTH_OPTIONS];
  }
  if (LONG_LENGTH_PRODUCTS.has(productType)) {
    return [...LONG_LENGTH_OPTIONS];
  }
  return [];
};

/**
 * Macro Theme and Denim are available for every style product type.
 * Keep in sync with backend `imageGeneratorDenim.ts`.
 */
const normalizeProductTypeKey = (value: string): string =>
  value
    .toLowerCase()
    .replace(/t-shirts?/g, "tshirts")
    .replace(/t shirts?/g, "tshirts")
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const isJeansProductType = (productType?: string): boolean => {
  if (!productType) {
    return false;
  }
  return normalizeProductTypeKey(productType).includes("jeans");
};

export const isDenimEligibleCategory = (category?: string): boolean =>
  category === "Western RTW";

/** Denim path is Western-only (Ethnic always uses Macro Theme). */
export const isDenimEligibleProductType = (
  productType?: string,
  category?: string
): boolean =>
  Boolean(productType?.trim()) &&
  (category === undefined || isDenimEligibleCategory(category));

export const STARTING_DIRECTION_OPTIONS = [
  { value: "macro-theme", label: "Macro Theme" },
  { value: "denim", label: "Denim" },
] as const;

export type ImageGeneratorStartingDirection =
  (typeof STARTING_DIRECTION_OPTIONS)[number]["value"];

export const DENIM_PALETTES = [
  { id: "ecru", label: "Ecru", hexes: ["#E8D9C4"] },
  { id: "light-blue", label: "Light blue", hexes: ["#C9D9E8"] },
  { id: "pale-mid-blue", label: "Pale Mid Blue", hexes: ["#A3BFD4"] },
  { id: "medium-blue", label: "Medium Blue", hexes: ["#5E8AAD"] },
  { id: "washed-indigo", label: "Washed Indigo", hexes: ["#3A5F80"] },
  { id: "deep-indigo", label: "Deep Indigo", hexes: ["#1A2B40"] },
] as const;

export const DENIM_MATERIALS = [
  { id: "lyocell-denim", label: "Lyocell Denim" },
  { id: "6-oz-lightweight", label: "6 Oz Lightweight" },
  { id: "10-oz-medium-weight", label: "10 Oz Medium weight" },
  { id: "14-oz-heavy-weight", label: "14 Oz Heavy weight" },
] as const;

export const DENIM_WASHES = [
  { id: "stone-wash", label: "Stone Wash" },
  { id: "acid-wash", label: "Acid Wash" },
  { id: "distressed", label: "Distressed" },
  { id: "bleach", label: "Bleach" },
  { id: "ice-wash", label: "Ice Wash" },
  { id: "tinted", label: "Tinted" },
  { id: "raw-denim", label: "Raw Denim" },
  { id: "metallic", label: "Metallic" },
] as const;

export const denimAssetApiPath = (kind: "materials" | "washes", id: string) =>
  `image-generator/denim-assets/${kind}/${id}`;

/**
 * Women Western RTW follow-ups (sheet).
 * Field keys unchanged for API: neckline | sleeve | material | fit | wash | length.
 */
const WESTERN_FOLLOW_UPS_WOMEN: Record<string, FollowUpField[]> = {
  Tshirts: ["neckline", "sleeve", "material", "length"],
  Tops: ["neckline", "sleeve", "material", "length"],
  "Coord Set": ["neckline", "sleeve", "material", "length"],
  "Co-ord Sets": ["neckline", "sleeve", "material", "length"],
  Dresses: ["neckline", "sleeve", "material", "length"],
  Overalls: ["material", "fit", "wash"],
  Jeans: ["material", "fit", "wash"],
  Trousers: ["material", "fit", "wash"],
  Skirts: ["neckline", "sleeve", "material", "length"],
  Blazers: ["neckline", "sleeve", "material"],
  Jackets: ["neckline", "sleeve", "material", "length"],
  Sweaters: ["neckline", "sleeve", "material", "length"],
  Sweatshirts: ["neckline", "sleeve", "material", "length"],
  Vests: ["material", "fit", "wash"],
  Shorts: ["material", "fit", "wash"],
};

/**
 * Men Western RTW follow-ups (sheet).
 * Overalls / Vests are NSM here (unlike Women Western bottoms).
 */
const WESTERN_FOLLOW_UPS_MEN: Record<string, FollowUpField[]> = {
  Tshirts: ["neckline", "sleeve", "material", "length"],
  Shirts: ["neckline", "sleeve", "material", "length"],
  Jeans: ["material", "fit", "wash"],
  Trousers: ["material", "fit", "wash"],
  Overalls: ["neckline", "sleeve", "material"],
  Blazers: ["neckline", "sleeve", "material"],
  Jackets: ["neckline", "sleeve", "material", "length"],
  Sweaters: ["neckline", "sleeve", "material", "length"],
  Sweatshirts: ["neckline", "sleeve", "material"],
  Vests: ["neckline", "sleeve", "material"],
  Shorts: ["material", "fit", "wash"],
};

/**
 * Women Ethnic RTW follow-ups (sheet).
 * Women-only products (Dresses, Skirts, Sarees) live only in Women PRODUCT_TYPES.
 */
const ETHNIC_FOLLOW_UPS_WOMEN: Record<string, FollowUpField[]> = {
  Tops: ["neckline", "sleeve", "material", "length"],
  Dresses: ["neckline", "sleeve", "material", "length"],
  Kurtas: ["neckline", "sleeve", "material", "length"],
  "Kurta Sets (3-piece)": ["neckline", "sleeve", "material", "length"],
  Overalls: ["neckline", "sleeve", "material"],
  Trousers: ["material", "fit"],
  Skirts: ["material", "fit", "length"],
  Jackets: ["neckline", "sleeve", "material", "length"],
  Vests: ["neckline", "sleeve", "material"],
  Sarees: ["neckline", "sleeve", "material"],
  Shorts: ["material", "fit"],
};

/**
 * Men Ethnic RTW follow-ups (sheet).
 * Product list is separate — no Dresses / Skirts / Sarees for Men.
 */
const ETHNIC_FOLLOW_UPS_MEN: Record<string, FollowUpField[]> = {
  Shirts: ["neckline", "sleeve", "material", "length"],
  Kurtas: ["neckline", "sleeve", "material", "length"],
  "Kurta Sets (3-piece)": ["neckline", "sleeve", "material", "length"],
  Overalls: ["neckline", "sleeve", "material"],
  Trousers: ["material", "fit"],
  Jackets: ["neckline", "sleeve", "material", "length"],
  Vests: ["neckline", "sleeve", "material"],
  Shorts: ["material", "fit"],
};

export const getFollowUpFields = (
  market: string,
  category: string,
  productType: string,
  influence?: string,
  startingDirection?: string
): FollowUpField[] => {
  if (influence === FORECAST_INFLUENCE || !productType) {
    return [];
  }

  const resolved = STYLE_PRODUCT_ALIASES[productType] || productType;
  let fields: FollowUpField[] = [];

  if (category === "Ethnic RTW") {
    const ethnicMap =
      market === "Men" ? ETHNIC_FOLLOW_UPS_MEN : ETHNIC_FOLLOW_UPS_WOMEN;
    fields = ethnicMap[resolved]
      ? [...ethnicMap[resolved]]
      : ["neckline", "sleeve", "material"];
  } else {
    const westernMap =
      market === "Men" ? WESTERN_FOLLOW_UPS_MEN : WESTERN_FOLLOW_UPS_WOMEN;
    if (westernMap[resolved]) {
      fields = [...westernMap[resolved]];
    } else if (BOTTOM_PRODUCTS.has(resolved)) {
      fields = ["material", "fit", "wash"];
    } else {
      fields = ["neckline", "sleeve", "material"];
      if (getLengthOptions(resolved).length) {
        fields.push("length");
      }
    }
  }

  // Never gate unlock on Length when the product has no length options
  // (dropdown stays disabled forever otherwise).
  if (!getLengthOptions(resolved).length) {
    fields = fields.filter(field => field !== "length");
  }

  if (startingDirection === "denim") {
    return fields.filter(field => field !== "material" && field !== "wash");
  }
  return fields;
};

/**
 * Image Generator only. Library / undefined influence delegates to getFollowUpFields.
 * Forecast and Upload Specific image offer Material + Length only (denim still
 * drops material; length omitted when the product has no length options).
 * Collection Build keeps calling getFollowUpFields, which returns none for New trend.
 */
export const getImageGeneratorFollowUpFields = (
  market: string,
  category: string,
  productType: string,
  influence?: string,
  startingDirection?: string
): FollowUpField[] => {
  if (isMaterialLengthOnlyInfluence(influence)) {
    if (!productType) {
      return [];
    }
    const resolved = STYLE_PRODUCT_ALIASES[productType] || productType;
    const fields: FollowUpField[] = [];
    if (startingDirection !== "denim") {
      fields.push("material");
    }
    if (getLengthOptions(resolved).length) {
      fields.push("length");
    }
    return fields;
  }
  return getFollowUpFields(
    market,
    category,
    productType,
    influence,
    startingDirection
  );
};

// 4. Pattern Formats & Embroidery Options (100% Excel Dataset)
export const GARMENT_FORMATS = ["Print", "Embroidery"] as const;

export const PATTERN_GARMENT_FORMATS = [
  "Print",
  "Embroidery",
  "Graphic",
] as const;

/** Subtypes for Print / Embroidery. */
export const PATTERN_REPEATS = [
  "AOP",
  "Engineered",
  "Placement",
  "Panel",
  "Border",
] as const;

/** Graphic format only allows Placement + Engineered. */
export const PATTERN_GRAPHIC_REPEATS = ["Placement", "Engineered"] as const;

export const PATTERN_AOP_FOLLOW_UPS = [
  "Straight",
  "Half Drop",
  "Offset",
  "Mirror",
  "Scatter",
  "Jaal",
] as const;

export const PATTERN_ENGINEERED_FOLLOW_UPS = [
  "Yoke",
  "Sleeve",
  "Free text",
] as const;

export const PATTERN_PLACEMENT_FOLLOW_UPS = [
  "Chest",
  "Back",
  "Free text",
] as const;

export const PATTERN_BORDER_FOLLOW_UPS = [
  "Bottom Hem",
  "Sleeve Hem",
  "Neckline",
] as const;

export const PATTERN_OFFSETS = ["25%", "33%", "50%"] as const;

export const PATTERN_DENSITIES = ["High", "Medium", "Low"] as const;

/** Existing trailing filters — leave option lists as-is. */
export const PATTERN_SCALES = [
  "Ditsy",
  "Mid-scale",
  "Statement",
  "Borders",
] as const;

export const PATTERN_COLOR_COUNTS = [
  "3 Colors",
  "4 Colors",
  "6 Colors",
  "Unlimited",
] as const;

export const PATTERN_PROCESSES = ["Block Print", "Screen", "Digital"] as const;

export const getPatternSubtypes = (
  garmentFormat: string
): readonly string[] => {
  if (!garmentFormat) {
    return [];
  }
  return garmentFormat === "Graphic"
    ? PATTERN_GRAPHIC_REPEATS
    : PATTERN_REPEATS;
};

export const getPatternFollowUpOptions = (
  subtype: string
): readonly string[] => {
  switch (subtype) {
    case "AOP":
      return PATTERN_AOP_FOLLOW_UPS;
    case "Engineered":
      return PATTERN_ENGINEERED_FOLLOW_UPS;
    case "Placement":
      return PATTERN_PLACEMENT_FOLLOW_UPS;
    case "Border":
      return PATTERN_BORDER_FOLLOW_UPS;
    default:
      return [];
  }
};

export const patternFollowUpNeedsFreeText = (
  subtype: string,
  followUp: string
): boolean => {
  if (subtype === "Panel") {
    return true;
  }
  return splitMultiValue(followUp).includes("Free text");
};

export const patternFollowUpIsMultiSelect = (subtype: string): boolean =>
  subtype === "Border";

export const patternOffsetVisible = (
  subtype: string,
  followUp: string
): boolean => subtype === "AOP" && splitMultiValue(followUp).includes("Offset");

/** Comma-separated list helpers (Level 2 multi-select + Border follow-ups). */
export const splitMultiValue = (value: string): string[] =>
  value
    .split(",")
    .map(item => item.trim())
    .filter(Boolean);

export const joinMultiValue = (items: readonly string[]): string =>
  items
    .map(item => item.trim())
    .filter(Boolean)
    .join(", ");

/**
 * Encode per-subtype maps for API string fields without a schema break.
 * Example: { Placement: "Chest", Border: "Bottom Hem, Sleeve Hem" }
 * → "Placement: Chest; Border: Bottom Hem, Sleeve Hem"
 */
export const serializeSubtypeMap = (map: Record<string, string>): string =>
  Object.entries(map)
    .filter(([, value]) => Boolean(value?.trim()))
    .map(([key, value]) => `${key}: ${value.trim()}`)
    .join("; ");

export const parseSubtypeMap = (value: string): Record<string, string> => {
  const result: Record<string, string> = {};
  if (!value?.trim()) {
    return result;
  }
  // Legacy single follow-up (no "Subtype: " prefix) → leave unscoped.
  if (!value.includes(":")) {
    result.__legacy = value.trim();
    return result;
  }
  value.split(";").forEach(part => {
    const trimmed = part.trim();
    if (!trimmed) {
      return;
    }
    const colon = trimmed.indexOf(":");
    if (colon <= 0) {
      return;
    }
    const key = trimmed.slice(0, colon).trim();
    const val = trimmed.slice(colon + 1).trim();
    if (key && val) {
      result[key] = val;
    }
  });
  return result;
};

/** Normalize stored patternRepeat to an ordered list of valid subtypes. */
export const parsePatternRepeats = (
  value: string,
  garmentFormat: string
): string[] => {
  const allowed = getPatternSubtypes(garmentFormat);
  const allowedSet = new Set(allowed);
  const selected = splitMultiValue(value).filter(item => allowedSet.has(item));
  return allowed.filter(item => selected.includes(item));
};

export const getFollowUpForSubtype = (
  encodedFollowUp: string,
  subtype: string,
  activeSubtypes?: readonly string[]
): string => {
  const map = parseSubtypeMap(encodedFollowUp);
  if (map[subtype]) {
    return map[subtype];
  }
  // Legacy single-value follow-up (no "Subtype: " prefix).
  if (map.__legacy && (!activeSubtypes || activeSubtypes.length <= 1)) {
    return map.__legacy;
  }
  return "";
};

export const setFollowUpForSubtype = (
  encodedFollowUp: string,
  subtype: string,
  nextValue: string,
  activeSubtypes: readonly string[]
): string => {
  const map = parseSubtypeMap(encodedFollowUp);
  delete map.__legacy;
  if (nextValue.trim()) {
    map[subtype] = nextValue.trim();
  } else {
    delete map[subtype];
  }
  const ordered: Record<string, string> = {};
  activeSubtypes.forEach(key => {
    if (map[key]) {
      ordered[key] = map[key];
    }
  });
  return serializeSubtypeMap(ordered);
};
export const EMBROIDERY_TYPES = [
  "3D Embroidery",
  "Aari Work",
  "Applique",
  "Blanket Stitch",
  "Chain Stitch Fill",
  "Cross Stitch",
  "Cutwork",
  "Dori Work",
  "French Knots",
  "Gota Patti",
  "Kasuti",
  "Mirror Work",
  "Running Stitch",
  "Sequins",
  "Silk Embroidery",
  "Zardozi",
  "Describe your own",
] as const;

/** Image Generation specification upload. PNG and JPEG, one image. */
export const SPECIFICATION_IMAGE_LABEL = "Specification image";
export const SPECIFICATION_IMAGE_ACCEPT =
  "image/png,image/jpeg,.png,.jpg,.jpeg";
export const SPECIFICATION_IMAGE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
] as const;
export const SPECIFICATION_IMAGE_FILE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(png|jpg)$/i;
export const SPECIFICATION_IMAGE_MAX_COUNT = 1;
export const SPECIFICATION_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
export const SPECIFICATION_IMAGE_UPLOAD_LABEL = "Upload specification";
export const SPECIFICATION_IMAGE_REPLACE_LABEL = "Replace image";
export const SPECIFICATION_IMAGE_REMOVE_LABEL = "Remove";
export const SPECIFICATION_IMAGE_UPLOADING_LABEL = "Uploading specification…";
export const SPECIFICATION_IMAGE_PREVIEW_ALT = "Specification image preview";
export const SPECIFICATION_IMAGE_TYPE_ERROR = "Upload a PNG or JPEG image.";
export const SPECIFICATION_IMAGE_SIZE_ERROR =
  "Specification image must be 8MB or smaller.";
export const SPECIFICATION_IMAGE_UPLOAD_ERROR =
  "The specification image could not be uploaded. Try again.";
export const SPECIFICATION_IMAGE_DELETE_ERROR =
  "The specification image could not be removed. Try again.";
export const SPECIFICATION_IMAGE_PENDING_ERROR =
  "Wait until the specification image finishes uploading.";
